import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Loader2, ArrowLeft, User, ShieldCheck, Download, Trash2 } from "lucide-react";

function PField({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label}</Label>
      <Input type={type} value={value} onChange={onChange} data-testid={testid} placeholder={placeholder} />
    </div>
  );
}

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [f, setF] = useState({
    name: user?.name || "", email: user?.email || "", phone: user?.phone || "",
    address_line1: user?.address_line1 || "", address_line2: user?.address_line2 || "",
    town: user?.town || "", county: user?.county || "", postcode: user?.postcode || "",
    nok_name: user?.nok_name || "", nok_phone: user?.nok_phone || "",
  });
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const loadRequests = () => api.get("/me/data-requests").then(({ data }) => setRequests(data)).catch(() => {});
  useEffect(() => { loadRequests(); }, []);

  const pwStrength = (p) => {
    let score = 0;
    if (p.length >= 8) score++;
    if (p.length >= 12) score++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
    if (/\d/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 2) return { label: "Weak", color: "bg-red-500", text: "text-red-600", w: "33%" };
    if (score === 3) return { label: "Okay", color: "bg-amber-500", text: "text-amber-600", w: "66%" };
    return { label: "Strong", color: "bg-green-500", text: "text-green-600", w: "100%" };
  };

  const save = async () => {
    if (newPassword && newPassword !== confirm) { toast.error("Your new passwords don't match."); return; }
    if (newPassword && (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword))) {
      toast.error("Your new password needs at least 8 characters, including a letter and a number."); return;
    }
    const payload = { ...f };
    if (currentPassword) payload.current_password = currentPassword;
    if (newPassword) payload.new_password = newPassword;
    setBusy(true);
    try {
      await api.patch("/me", payload);
      await refreshUser();
      toast.success("Your profile has been updated.");
      setCurrentPassword(""); setNewPassword(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update your profile.");
    } finally { setBusy(false); }
  };

  const requestData = async (kind) => {
    const msg = kind === "deletion"
      ? "Request deletion of your account and personal data? The committee will review and action this. Club records required for governance may be anonymised rather than erased."
      : "Request a copy of all the information we hold about you (profile, address, next of kin, grades, bookings)? The committee will prepare and send it to you.";
    if (!window.confirm(msg)) return;
    try {
      await api.post("/me/data-request", { kind });
      toast.success(kind === "deletion" ? "Deletion request sent to the committee." : "Data request sent to the committee.");
      loadRequests();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const backTo = user && (user.role === "admin" || user.role === "committee") ? "/admin" : "/member";
  const openAccess = requests.find((r) => r.kind === "access" && r.status === "open");
  const openDeletion = requests.find((r) => r.kind === "deletion" && r.status === "open");
  const st = newPassword ? pwStrength(newPassword) : null;
  const needsMix = newPassword && newPassword.length >= 8 && !(/[A-Za-z]/.test(newPassword) && /\d/.test(newPassword));

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-xl mx-auto px-4 py-10 space-y-6">
        <Link to={backTo} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#0F2744]" data-testid="profile-back"><ArrowLeft className="h-4 w-4" /> Back to console</Link>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8" data-testid="profile-page">
          <h1 className="text-2xl font-extrabold text-[#0F2744] flex items-center gap-2"><User className="h-6 w-6" /> Your profile</h1>
          <p className="text-sm text-slate-500 mt-1">Update your contact, address and emergency details.</p>

          <div className="mt-6 space-y-4">
            <PField label="Full name" value={f.name} onChange={(e) => set("name", e.target.value)} testid="profile-name" />
            <PField label="Email address" value={f.email} onChange={(e) => set("email", e.target.value)} type="email" testid="profile-email" />
            <PField label="Phone" value={f.phone} onChange={(e) => set("phone", e.target.value)} testid="profile-phone" placeholder="e.g. 07700 900000" />

            <div className="pt-2 border-t border-slate-100">
              <p className="text-sm font-semibold text-[#0F2744] mt-3 mb-3">Home address</p>
              <div className="space-y-3">
                <PField label="Address line 1" value={f.address_line1} onChange={(e) => set("address_line1", e.target.value)} testid="profile-address1" />
                <PField label="Address line 2" value={f.address_line2} onChange={(e) => set("address_line2", e.target.value)} testid="profile-address2" />
                <div className="grid grid-cols-2 gap-3">
                  <PField label="Town / city" value={f.town} onChange={(e) => set("town", e.target.value)} testid="profile-town" />
                  <PField label="County" value={f.county} onChange={(e) => set("county", e.target.value)} testid="profile-county" />
                </div>
                <PField label="Postcode" value={f.postcode} onChange={(e) => set("postcode", e.target.value)} testid="profile-postcode" />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <p className="text-sm font-semibold text-[#0F2744] mt-3 mb-3">Emergency contact (next of kin)</p>
              <div className="grid grid-cols-2 gap-3">
                <PField label="Name" value={f.nok_name} onChange={(e) => set("nok_name", e.target.value)} testid="profile-nok-name" />
                <PField label="Phone" value={f.nok_phone} onChange={(e) => set("nok_phone", e.target.value)} testid="profile-nok-phone" placeholder="e.g. 07700 900000" />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-100">
              <p className="text-sm font-semibold text-[#0F2744] mt-3">Change password</p>
              <p className="text-xs text-slate-400 mb-3">Leave blank to keep your current password. Changing your email or password needs your current password.</p>
              <div className="space-y-3">
                <div><Label className="mb-1.5 block">Current password</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} data-testid="profile-current-password" /></div>
                <div>
                  <Label className="mb-1.5 block">New password</Label>
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="profile-new-password" />
                  {st && (
                    <div className="mt-1.5" data-testid="pw-strength">
                      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${st.color} transition-all`} style={{ width: st.w }} /></div>
                      <p className={`text-xs mt-1 ${needsMix ? "text-amber-600" : st.text}`}>{newPassword.length < 8 ? "Too short — use at least 8 characters" : needsMix ? "Add a letter and a number" : `${st.label} password`}</p>
                    </div>
                  )}
                </div>
                <div><Label className="mb-1.5 block">Confirm new password</Label><Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="profile-confirm-password" /></div>
              </div>
            </div>
            <Button onClick={save} disabled={busy} className="bg-[#0F2744] hover:bg-[#0B192C] w-full" data-testid="profile-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 sm:p-8" data-testid="profile-privacy">
          <h2 className="text-lg font-extrabold text-[#0F2744] flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-green-600" /> Your data &amp; privacy</h2>
          <p className="text-sm text-slate-500 mt-1 mb-4">Under data protection law you can ask for a copy of the information we hold about you (including your address and next-of-kin details), or ask us to delete your account.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => requestData("access")} disabled={!!openAccess} className="border-[#0F2744] text-[#0F2744] hover:bg-slate-50" data-testid="request-data-btn">
              <Download className="h-4 w-4 mr-1.5" /> {openAccess ? "Data request pending" : "Request my data"}
            </Button>
            <Button variant="outline" onClick={() => requestData("deletion")} disabled={!!openDeletion} className="border-red-400 text-red-600 hover:bg-red-50" data-testid="request-deletion-btn">
              <Trash2 className="h-4 w-4 mr-1.5" /> {openDeletion ? "Deletion requested" : "Request account deletion"}
            </Button>
          </div>
          {requests.length > 0 && (
            <div className="mt-4 space-y-1.5">
              {requests.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-sm" data-testid={`my-request-${r.id}`}>
                  <span className="text-slate-600 capitalize">{r.kind === "access" ? "Data access" : "Account deletion"} request</span>
                  <Badge className={r.status === "open" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>{r.status === "open" ? "Pending" : "Completed"}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
